import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  message,
  Popconfirm,
  Collapse,
  Typography,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  StopOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

interface FiscalPeriod {
  id: string;
  name: string;
  code: string;
  dateStart: string;
  dateEnd: string;
  state: string;
}

interface FiscalYear {
  id: string;
  name: string;
  dateStart: string;
  dateEnd: string;
  state: string;
  periods: FiscalPeriod[];
  createdAt: string;
}

const FiscalYearManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFiscalYear, setEditingFiscalYear] = useState<FiscalYear | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  const fetchFiscalYears = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/accounting/fiscal-years');
      setFiscalYears(response.data || []);
    } catch (error: any) {
      message.error('Failed to load fiscal years');
    } finally {
      setLoading(false);
    }
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      OPEN: 'green',
      CLOSED: 'red'
    };
    return colors[state] || 'default';
  };

  const handleCreate = () => {
    setEditingFiscalYear(null);
    form.resetFields();
    form.setFieldsValue({
      generatePeriods: true
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: FiscalYear) => {
    setEditingFiscalYear(record);
    form.setFieldsValue({
      name: record.name,
      dateRange: [dayjs(record.dateStart), dayjs(record.dateEnd)]
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [dateStart, dateEnd] = values.dateRange;

      const data = {
        name: values.name,
        dateStart: dateStart.format('YYYY-MM-DD'),
        dateEnd: dateEnd.format('YYYY-MM-DD'),
        generatePeriods: values.generatePeriods
      };

      if (editingFiscalYear) {
        await apiService.put(`/accounting/fiscal-years/${editingFiscalYear.id}`, data);
        message.success('Fiscal year updated');
      } else {
        await apiService.post('/accounting/fiscal-years', data);
        message.success('Fiscal year created');
      }

      setIsModalVisible(false);
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleOpen = async (id: string) => {
    try {
      await apiService.post(`/accounting/fiscal-years/${id}/open`);
      message.success('Fiscal year opened');
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Failed to open fiscal year');
    }
  };

  const handleClose = async (id: string) => {
    try {
      await apiService.post(`/accounting/fiscal-years/${id}/close`);
      message.success('Fiscal year closed');
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Failed to close fiscal year');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/accounting/fiscal-years/${id}`);
      message.success('Fiscal year deleted');
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete fiscal year');
    }
  };

  const handleOpenPeriod = async (periodId: string) => {
    try {
      await apiService.post(`/accounting/fiscal-periods/${periodId}/open`);
      message.success('Period opened');
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Failed to open period');
    }
  };

  const handleClosePeriod = async (periodId: string) => {
    try {
      await apiService.post(`/accounting/fiscal-periods/${periodId}/close`);
      message.success('Period closed');
      fetchFiscalYears();
    } catch (error: any) {
      message.error(error.message || 'Failed to close period');
    }
  };

  const columns: ColumnsType<FiscalYear> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <CalendarOutlined />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Start Date',
      dataIndex: 'dateStart',
      key: 'dateStart',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'End Date',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Periods',
      dataIndex: 'periods',
      key: 'periods',
      render: (periods: FiscalPeriod[]) => (
        <Text>{periods?.length || 0} periods</Text>
      )
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => (
        <Tag color={getStateColor(state)}>{state}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.state === 'DRAFT' && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Open">
                <Popconfirm
                  title="Open this fiscal year?"
                  description="This will make it active for accounting entries."
                  onConfirm={() => handleOpen(record.id)}
                >
                  <Button type="text" icon={<PlayCircleOutlined />} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this fiscal year?"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </>
          )}
          {record.state === 'OPEN' && (
            <Tooltip title="Close">
              <Popconfirm
                title="Close this fiscal year?"
                description="Ensure all periods are closed first."
                onConfirm={() => handleClose(record.id)}
              >
                <Button type="text" icon={<StopOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const periodColumns: ColumnsType<FiscalPeriod> = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Start Date',
      dataIndex: 'dateStart',
      key: 'dateStart',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'End Date',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => <Tag color={getStateColor(state)}>{state}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          {record.state === 'DRAFT' && (
            <Button size="small" onClick={() => handleOpenPeriod(record.id)}>Open</Button>
          )}
          {record.state === 'OPEN' && (
            <Button size="small" onClick={() => handleClosePeriod(record.id)}>Close</Button>
          )}
        </Space>
      )
    }
  ];

  const expandedRowRender = (record: FiscalYear) => (
    <Table
      columns={periodColumns}
      dataSource={record.periods}
      rowKey="id"
      pagination={false}
      size="small"
    />
  );

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          <span>Fiscal Year Management</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          New Fiscal Year
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={fiscalYears}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => record.periods?.length > 0
        }}
        pagination={false}
      />

      <Modal
        title={editingFiscalYear ? 'Edit Fiscal Year' : 'New Fiscal Year'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., FY 2025" />
          </Form.Item>
          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          {!editingFiscalYear && (
            <Form.Item
              name="generatePeriods"
              label="Auto-generate Monthly Periods"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default FiscalYearManagement;
